import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, AppBar, Box, Button, Chip, CircularProgress, Container, Paper, Slider, Stack, Tab, Tabs,
    TextField, ToggleButton, ToggleButtonGroup, Toolbar, Tooltip, Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../api/axios';

/* Reglas de repaso espaciado de English Daily y AWS Daily (n8n): tras subir al paso n, el siguiente repaso llega en INTERVALOS[n] días. */
const INTERVALOS = [1, 3, 7, 14, 30];
const NIVEL_PASO = { 'Paso 3': 3, Dominada: 5 };
const EXAM_WEIGHTS = { Seguridad: 30, Resiliencia: 26, Rendimiento: 24, Costos: 20 };
const DOMINIOS = Object.keys(EXAM_WEIGHTS);
const DAY = 86400000;
const C = { en: '#2a78d6', aws: '#eb6834', seq: ['#eef1f5', '#b7d3f6', '#6da7ec', '#2a78d6', '#184f95'], line: '#e2e8f0', muted: '#64748b' };

const fmtDay = new Intl.DateTimeFormat('es-GT', { day: 'numeric', month: 'short' });
const fmtDayTime = new Intl.DateTimeFormat('es-GT', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
const fmtLong = new Intl.DateTimeFormat('es-GT', { day: 'numeric', month: 'long', year: 'numeric' });

function parseDate(s) { if (!s) return null; return s.includes('T') ? new Date(s) : new Date(`${s}T00:00:00`); }
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { return new Date(d.getTime() + n * DAY); }
function daysBetween(a, b) { return Math.round((startOfDay(b) - startOfDay(a)) / DAY); }
function dayKey(d) { return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; }
function isoDay(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
/** Días desde el próximo acierto hasta llegar al paso objetivo, si aciertas cada repaso. */
function daysToReach(paso, target) { let d = 0; for (let k = paso + 1; k < target; k++) d += INTERVALOS[k]; return d; }

/* ---------------------------------------------------------------- piezas */
function Card({ title, desc, children, sx }) {
    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0, ...sx }}>
            {title && <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>}
            {desc && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{desc}</Typography>}
            {children}
        </Paper>
    );
}

function Kpi({ value, suffix, label }) {
    return (
        <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 30, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                {value}{suffix && <Typography component="span" color="text.secondary" sx={{ fontSize: 16, fontWeight: 600 }}>{suffix}</Typography>}
            </Typography>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
    );
}

function Big({ value, label }) {
    return (
        <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 24, lineHeight: 1.15 }}>{value}</Typography>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
    );
}

function KpiRow({ children, note }) {
    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 2 }}>{children}</Box>
            {note && <Typography variant="body2" color="text.secondary" sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>{note}</Typography>}
        </Paper>
    );
}

function NextChip({ next, done, today }) {
    if (done) return <Chip size="small" color="success" variant="outlined" label="✓ Dominada" />;
    if (!next) return <Chip size="small" label="—" />;
    const d = daysBetween(today, next);
    if (d < 0) return <Chip size="small" color="warning" label={`⚠ Atrasado ${-d} d`} />;
    if (d === 0) return <Chip size="small" color="warning" variant="outlined" label="Hoy" />;
    return <Chip size="small" label={fmtDay.format(next)} />;
}

function ActivityStrip({ dates, label, today }) {
    const start = addDays(today, -20);
    const counts = {};
    dates.forEach((d) => { if (d) counts[dayKey(d)] = (counts[dayKey(d)] || 0) + 1; });
    return (
        <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(21, minmax(0, 1fr))', gap: '3px' }}>
                {Array.from({ length: 21 }, (_, i) => {
                    const d = addDays(start, i);
                    const n = counts[dayKey(d)] || 0;
                    return (
                        <Tooltip key={i} arrow title={`${fmtDay.format(d)}: ${n} ${label}`}>
                            <Box tabIndex={0} aria-label={`${fmtDay.format(d)}: ${n} ${label}`} sx={{
                                aspectRatio: '1', borderRadius: '4px', bgcolor: C.seq[Math.min(n, 4)],
                                outline: i === 20 ? `1.5px solid ${C.muted}` : 'none', outlineOffset: -1.5,
                                '&:hover, &:focus-visible': { boxShadow: '0 0 0 2px #0f172a' },
                            }} />
                        </Tooltip>
                    );
                })}
            </Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75, fontSize: 12, color: 'text.secondary' }}>
                <span>{fmtDay.format(start)}</span><span>hoy</span>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1, fontSize: 12, color: 'text.secondary' }}>
                <span>0</span>{C.seq.map((c) => <Box key={c} sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: c }} />)}<span>4+</span>
            </Stack>
        </Box>
    );
}

function useWidth() {
    const ref = useRef(null);
    const [w, setW] = useState(480);
    useEffect(() => {
        if (!ref.current) return undefined;
        const ro = new ResizeObserver(([e]) => setW(Math.max(260, Math.round(e.contentRect.width))));
        ro.observe(ref.current);
        return () => ro.disconnect();
    }, []);
    return [ref, w];
}

function ErrorChart({ log }) {
    const [ref, W] = useWidth();
    const pts = log.filter((r) => r.palabras > 0).map((r) => ({ ...r, v: (r.errores / r.palabras) * 100 }));
    if (!pts.length) return <Typography variant="body2" color="text.secondary">Aún no hay respuestas corregidas.</Typography>;
    const H = 200, m = { l: 34, r: 14, t: 16, b: 26 };
    const iw = W - m.l - m.r, ih = H - m.t - m.b;
    const maxV = Math.max(10, Math.ceil(Math.max(...pts.map((p) => p.v)) / 10) * 10);
    const step = maxV <= 40 ? 10 : 20;
    const x = (i) => m.l + (pts.length === 1 ? iw / 2 : (i * iw) / (pts.length - 1));
    const y = (v) => m.t + ih - (v / maxV) * ih;
    const grid = []; for (let v = 0; v <= maxV; v += step) grid.push(v);
    const labels = []; let lastKey = null, lastX = -999;
    pts.forEach((p, i) => {
        const k = dayKey(p.d);
        if (k !== lastKey && x(i) - lastX > 56) { labels.push({ i, text: fmtDay.format(p.d) }); lastKey = k; lastX = x(i); }
    });
    const line = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.v)}`).join(' ');
    const last = pts[pts.length - 1];
    return (
        <Box ref={ref} sx={{ width: '100%' }}>
            <svg width={W} height={H} role="img" aria-label="Errores por cada 100 palabras en cada respuesta" style={{ display: 'block', overflow: 'visible' }}>
                {grid.map((v) => (
                    <g key={v}>
                        <line x1={m.l} x2={W - m.r} y1={y(v)} y2={y(v)} stroke={C.line} />
                        <text x={m.l - 6} y={y(v) + 4} textAnchor="end" fontSize="11" fill={C.muted}>{v}</text>
                    </g>
                ))}
                {labels.map(({ i, text }) => (
                    <text key={i} x={x(i)} y={H - 6} textAnchor={i === 0 ? 'start' : 'middle'} fontSize="11" fill={C.muted}>{text}</text>
                ))}
                {pts.length > 1 && <path d={`${line} L${x(pts.length - 1)},${y(0)} L${x(0)},${y(0)} Z`} fill={C.en} fillOpacity="0.08" />}
                {pts.length > 1 && <path d={line} fill="none" stroke={C.en} strokeWidth="2" strokeLinejoin="round" />}
                {pts.map((p, i) => (
                    <Tooltip key={i} arrow title={`${fmtDayTime.format(p.d)} · ${p.v.toFixed(0)} por 100 palabras (${p.errores} errores en ${p.palabras}) · ${p.tema}`}>
                        <g tabIndex={0}>
                            <rect x={x(i) - 12} y={m.t} width="24" height={ih} fill="transparent" />
                            <circle cx={x(i)} cy={y(p.v)} r={i === pts.length - 1 ? 5 : 4} fill={C.en} stroke="#fff" strokeWidth="2" />
                        </g>
                    </Tooltip>
                ))}
                <text x={x(pts.length - 1) - 8} y={y(last.v) - 10} textAnchor="end" fontSize="12" fontWeight="600" fill="#0f172a">{last.v.toFixed(0)}</text>
            </svg>
        </Box>
    );
}

function Bars({ rows }) {
    return (
        <Stack spacing={1.5}>
            {rows.map((r) => (
                <Box key={r.name} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1.2fr) minmax(0, 1fr) auto' }, gap: { xs: 0.5, sm: 1.5 }, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ minWidth: 0 }}>{r.name}</Typography>
                    <Tooltip arrow title={r.num}>
                        <Box sx={{ height: 10, bgcolor: C.seq[0], borderRadius: 5, overflow: 'hidden', display: 'flex', gap: '2px' }}>
                            {r.segs.filter((s) => s.n > 0).map((s, i) => (
                                <Box key={i} sx={{ width: `${(s.n / r.total) * 100}%`, bgcolor: s.color, height: '100%' }} />
                            ))}
                        </Box>
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', textAlign: { sm: 'right' }, minWidth: 90 }}>{r.num}</Typography>
                </Box>
            ))}
        </Stack>
    );
}

function Table({ head, children, empty }) {
    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{
                width: '100%', borderCollapse: 'collapse', fontSize: 14,
                '& th': { textAlign: 'left', fontSize: 11.5, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary', fontWeight: 600, px: 1.25, pb: 1, borderBottom: 1, borderColor: 'divider', whiteSpace: 'nowrap' },
                '& td': { px: 1.25, py: 1.1, borderBottom: 1, borderColor: 'divider', verticalAlign: 'top' },
                '& tr:last-child td': { borderBottom: 0 },
                '& td.n': { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
            }}>
                <thead><tr>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                <tbody>{children}</tbody>
            </Box>
            {empty}
        </Box>
    );
}

function Pips({ paso }) {
    return (
        <Box component="span" aria-label={`Paso ${paso} de 5`} sx={{ display: 'inline-flex', gap: '3px' }}>
            {[0, 1, 2, 3, 4].map((i) => (
                <Box key={i} component="i" sx={{ width: 9, height: 9, borderRadius: '2px', border: 1, borderColor: i < paso ? C.en : 'divider', bgcolor: i < paso ? C.en : C.seq[0] }} />
            ))}
        </Box>
    );
}

/* ---------------------------------------------------------------- inglés */
function English({ data, today }) {
    const log = useMemo(() => (data.en_log || []).map((r) => ({ ...r, d: parseDate(r.fecha) })).filter((r) => r.d).sort((a, b) => a.d - b.d), [data]);
    const structs = useMemo(() => (data.en_struct || []).map((s) => ({ ...s, next: parseDate(s.proximo) })), [data]);
    const last = log[log.length - 1];
    const mastered = structs.filter((s) => s.estado === 'Dominada').length;
    const open = structs.filter((s) => s.estado !== 'Dominada');
    const due = open.filter((s) => s.next && s.next <= today).length;
    const foco = structs.find((s) => s.foco);
    const gap = last ? daysBetween(last.d, today) : null;
    const note = [
        last && (gap === 0 ? 'Respondiste hoy.' : gap === 1 ? 'Última respuesta: ayer.' : `Última respuesta hace ${gap} días.`),
        due ? `${due} estructuras esperan repaso.` : null,
        foco ? `Foco de la semana: ${foco.name || foco.nombre}.` : null,
    ].filter(Boolean).join(' ');

    let eta = today, hits = 0;
    open.forEach((s) => { const t0 = s.next && s.next > today ? s.next : today; const e = addDays(t0, daysToReach(s.paso, 5)); if (e > eta) eta = e; hits += 5 - s.paso; });
    const hardest = [...open].sort((a, b) => b.fallos - a.fallos)[0];

    const top = Object.entries(log.reduce((m, r) => { r.estructuras.forEach((s) => { m[s] = (m[s] || 0) + 1; }); return m; }, {}))
        .sort((a, b) => b[1] - a[1]).slice(0, 6);

    return (
        <Stack spacing={2}>
            <KpiRow note={note}>
                <Kpi value={log.length} label="respuestas escritas" />
                <Kpi value={last && last.palabras ? ((last.errores / last.palabras) * 100).toFixed(0) : '–'} label="errores por cada 100 palabras (última)" />
                <Kpi value={mastered} suffix={` / ${structs.length}`} label="estructuras dominadas" />
            </KpiRow>

            <Card title="¿Cuándo podrías dominar todas tus estructuras?" desc="Mismas reglas que English Daily: cada acierto sube un paso y el siguiente repaso llega en 3, 7, 14 y 30 días. Un fallo la regresa a 0." sx={{ borderColor: '#94a3b8' }}>
                {open.length ? (
                    <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                            <Big value={fmtDay.format(eta)} label={`lo más pronto para dominar las ${open.length}`} />
                            <Big value={hits} label="repasos correctos que te faltan" />
                            <Big value={`${daysBetween(today, eta)} días`} label="desde hoy, sin fallar" />
                        </Box>
                        {hardest && <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>La que más te cuesta es <b>{hardest.nombre}</b> ({hardest.fallos} fallos). Cada fallo la regresa a 0 y suma unos 55 días.</Typography>}
                    </>
                ) : <Typography variant="body2">Todas tus estructuras están dominadas.</Typography>}
            </Card>

            <Card title="Días que respondiste" desc="Últimos 21 días. Cada cuadro cuenta las respuestas que corrigió English Daily.">
                <ActivityStrip dates={log.map((r) => r.d)} label="respuestas" today={today} />
            </Card>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Card title="Errores por cada 100 palabras" desc="Una marca por respuesta. Más abajo es mejor."><ErrorChart log={log} /></Card>
                <Card title="Errores que más se repiten" desc="Veces que apareció cada estructura en tus correcciones.">
                    {top.length ? <Bars rows={top.map(([name, n]) => ({ name, total: top[0][1], segs: [{ n, color: C.en }], num: `${n} ${n === 1 ? 'vez' : 'veces'}` }))} />
                        : <Typography variant="body2" color="text.secondary">Sin errores registrados.</Typography>}
                </Card>
            </Box>

            <Card title="Estructuras en repaso" desc="Paso sube 1 con cada acierto y vuelve a 0 con un fallo. Con 5 queda dominada.">
                <Table head={['Estructura', 'Tu frase → corrección', 'Fallos', 'Paso', 'Repaso']}>
                    {[...structs].sort((a, b) => (b.foco - a.foco) || (b.fallos - a.fallos)).map((s) => (
                        <tr key={s.id}>
                            <td>{s.nombre} {s.foco && <Chip size="small" label="Foco" sx={{ ml: 0.5 }} />}</td>
                            <td style={{ fontSize: 13 }}>
                                <Box component="s" sx={{ color: 'error.main' }}>{s.mi_frase}</Box> → <Box component="span" sx={{ color: 'success.main' }}>{s.correccion}</Box>
                            </td>
                            <td className="n">{s.fallos}</td>
                            <td><Pips paso={s.paso} /></td>
                            <td><NextChip next={s.next} done={s.estado === 'Dominada'} today={today} /></td>
                        </tr>
                    ))}
                </Table>
            </Card>
        </Stack>
    );
}

/* ---------------------------------------------------------------- AWS */
function GoalCard({ meta, metaError, plan, onSaved }) {
    const [fecha, setFecha] = useState(meta?.fecha || '2026-12-31');
    const [nivel, setNivel] = useState(meta?.nivel || 'Paso 3');
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    useEffect(() => { if (meta) { setFecha(meta.fecha || '2026-12-31'); setNivel(meta.nivel || 'Paso 3'); } }, [meta]);
    const dirty = !meta || fecha !== meta.fecha || nivel !== meta.nivel;

    const save = async () => {
        setSaving(true); setMsg(null);
        try {
            await api.patch('/estudio/meta', { fecha, nivel });
            setMsg({ ok: true, text: 'Meta guardada. AWS Daily recalcula los correos del día desde la próxima hora.' });
            onSaved();
        } catch (e) {
            setMsg({ ok: false, text: e?.response?.data?.message || 'No se pudo guardar la meta en Notion.' });
        } finally { setSaving(false); }
    };

    return (
        <Card title="Tu meta del examen" desc="Cámbiala cuando necesites correrla o aplazarla. Se guarda en Notion y AWS Daily ajusta cuántos correos te manda al día y qué contenido lleva cada uno." sx={{ borderColor: C.aws, borderWidth: 2 }}>
            {metaError && <Alert severity="warning" sx={{ mb: 2 }}>{metaError.message}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <TextField id="meta-fecha" label="Listo para el" type="date" size="small" value={fecha}
                    onChange={(e) => setFecha(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
                <ToggleButtonGroup size="small" exclusive value={nivel} onChange={(_, v) => v && setNivel(v)} aria-label="Nivel">
                    <ToggleButton value="Paso 3">Paso 3 (base sólida)</ToggleButton>
                    <ToggleButton value="Dominada">Dominada (5 aciertos)</ToggleButton>
                </ToggleButtonGroup>
                <Button variant="contained" onClick={save} disabled={saving || !dirty || !fecha}>{saving ? 'Guardando…' : 'Guardar meta'}</Button>
            </Stack>
            {msg && <Alert severity={msg.ok ? 'success' : 'error'} sx={{ mt: 2 }}>{msg.text}</Alert>}
            {plan && (
                <Box sx={{ mt: 2.5, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    {plan.imposible ? (
                        <Alert severity="error">Con el nivel “{plan.nivel}” ya no alcanzas para el {fmtLong.format(plan.meta)}: cada concepto nuevo necesita {plan.lead} días para llegar. Aplaza la fecha o baja el nivel.</Alert>
                    ) : (
                        <>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                                <Big value={`${plan.correosDia} al día`} label="correos que te enviará AWS Daily" />
                                <Big value={`${plan.requerido.toFixed(1)} / sem`} label="conceptos nuevos que necesitas" />
                                <Big value={`${plan.real.toFixed(1)} / sem`} label="tu ritmo real hasta hoy" />
                                <Big value={fmtDay.format(plan.ultimoNuevo)} label="último día para empezar conceptos nuevos" />
                            </Box>
                            <Alert sx={{ mt: 2 }} severity={plan.real >= plan.requerido ? 'success' : 'warning'}>
                                {plan.real >= plan.requerido
                                    ? `Vas a tiempo: con tu ritmo actual llegas antes del ${fmtLong.format(plan.meta)}.`
                                    : `Vas atrasado: a tu ritmo actual llegarías el ${fmtLong.format(plan.llegadaReal)}. AWS Daily alternará conceptos nuevos y repasos para ponerte al día.`}
                            </Alert>
                        </>
                    )}
                </Box>
            )}
        </Card>
    );
}

function AnswerCard({ log, pickId, onPick, onDone }) {
    /* Una por concepto: la más reciente sin responder (las repetidas del mismo concepto sobran) */
    const queue = useMemo(() => {
        const seenC = new Set();
        return log.filter((q) => q.resultado === 'Pendiente' && q.enunciado && Object.keys(q.opciones || {}).length)
            .filter((q) => (seenC.has(q.concepto) ? false : seenC.add(q.concepto)));
    }, [log]);
    const [letra, setLetra] = useState('');
    const [porque, setPorque] = useState('');
    const [estado, setEstado] = useState('responder'); // responder | enviando | calificando | listo | error
    const [result, setResult] = useState(null);
    const [msg, setMsg] = useState('');
    const [heldId, setHeldId] = useState(null);
    const ref = useRef(null);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);

    const q = log.find((x) => x.id === (heldId || pickId)) || queue[0];
    const idx = q ? queue.findIndex((x) => x.id === q.id) : -1;
    useEffect(() => { if (pickId) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [pickId]);

    const reset = (nextId) => { clearTimeout(timer.current); setLetra(''); setPorque(''); setEstado('responder'); setResult(null); setMsg(''); setHeldId(null); onPick(nextId || null); };
    const skip = () => { const n = queue[(idx + 1) % queue.length]; reset(n && n.id !== q.id ? n.id : null); };

    const poll = (id, tries) => {
        timer.current = setTimeout(async () => {
            try {
                const { data: r } = await api.get(`/estudio/aws/${id}`);
                if (r.resultado !== 'Pendiente') { setResult(r); setEstado('listo'); return; }
            } catch { /* reintenta */ }
            if (tries > 1) poll(id, tries - 1);
            else { setEstado('error'); setMsg('Tarda más de lo normal. Te llega igual por correo; toca Actualizar en un rato.'); }
        }, 5000);
    };
    const send = async () => {
        setEstado('enviando'); setMsg(''); setHeldId(q.id);
        try {
            await api.post(`/estudio/aws/${q.id}`, { letra, porque });
            setEstado('calificando');
            poll(q.id, 18);
        } catch (e) {
            setEstado('responder'); setMsg(e?.response?.data?.message || 'No se pudo enviar tu respuesta.');
        }
    };

    if (!q) {
        return (
            <Card title="Responder ahora" sx={{ borderColor: C.aws, borderWidth: 2 }}>
                <Typography color="text.secondary">🎉 No tienes preguntas pendientes. Pide otra desde el correo con “📩 Mándame otra”.</Typography>
            </Card>
        );
    }
    const bien = result?.resultado === 'Correcta';
    return (
        <Box ref={ref} sx={{ scrollMarginTop: 72 }}>
            <Card sx={{ borderColor: C.aws, borderWidth: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography sx={{ fontWeight: 700 }}>Responder ahora</Typography>
                    {queue.length > 0 && <Typography variant="body2" color="text.secondary">{idx >= 0 ? `${idx + 1} de ${queue.length}` : `${queue.length} pendientes`}</Typography>}
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                    <Chip size="small" label={q.concepto} />
                    <Chip size="small" variant="outlined" label={q.tipo === 'Nuevo' ? '🆕 Nuevo' : '🔁 Repaso'} />
                    <Chip size="small" variant="outlined" label={fmtDayTime.format(q.sent)} />
                </Stack>
                <Typography sx={{ fontSize: 17, lineHeight: 1.5, mb: 2 }}>{q.enunciado}</Typography>

                {estado !== 'listo' ? (
                    <>
                        <Stack spacing={1.25} role="radiogroup" aria-label="Opciones">
                            {['A', 'B', 'C', 'D'].filter((l) => q.opciones[l]).map((l) => (
                                <Button key={l} role="radio" aria-checked={letra === l} variant={letra === l ? 'contained' : 'outlined'}
                                    disabled={estado !== 'responder'} onClick={() => setLetra(l)}
                                    sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', fontSize: 16, py: 1.5, px: 2, minHeight: 52, gap: 1.5, alignItems: 'flex-start' }}>
                                    <b>{l})</b><span>{q.opciones[l]}</span>
                                </Button>
                            ))}
                        </Stack>
                        <TextField label="¿Por qué? (español o inglés)" multiline minRows={3} fullWidth value={porque}
                            onChange={(e) => setPorque(e.target.value)} disabled={estado !== 'responder'}
                            helperText="Responde de memoria. Si no sabes, di qué te confunde: también cuenta."
                            sx={{ mt: 2, '& textarea': { fontSize: 16 } }} />
                        {msg && <Alert severity={estado === 'error' ? 'warning' : 'error'} sx={{ mt: 2 }}>{msg}</Alert>}
                        {estado === 'calificando' && <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mt: 2 }}>Calificando tu respuesta… (unos 20 segundos)</Alert>}
                        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                            <Button variant="contained" size="large" onClick={send} disabled={!letra || estado !== 'responder'} sx={{ flex: 1, py: 1.5, bgcolor: C.aws, '&:hover': { bgcolor: '#c9541f' } }}>
                                {estado === 'enviando' ? 'Enviando…' : 'Enviar respuesta'}
                            </Button>
                            {estado === 'responder' && queue.length > 1 && <Button size="large" onClick={skip}>Saltar</Button>}
                            {estado === 'error' && <Button size="large" onClick={() => { reset(); onDone(); }}>Seguir</Button>}
                        </Stack>
                    </>
                ) : (
                    <>
                        <Alert severity={bien ? 'success' : 'error'} sx={{ fontSize: 16 }}>
                            <b>{bien ? '✅ ¡Correcta!' : '❌ Incorrecta'}</b> · Correcta: <b>{result.correcta}) {q.opciones[result.correcta] || ''}</b>
                        </Alert>
                        {result.feedback && <Typography sx={{ mt: 2, fontSize: 16, lineHeight: 1.5 }}>💬 {result.feedback}</Typography>}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>La explicación completa de cada opción te llega por correo.</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
                            {result.slides && <Button variant="outlined" size="large" href={result.slides} target="_blank" rel="noopener" sx={{ py: 1.5 }}>🎞️ Ver slides (listas en ~1 min)</Button>}
                            <Button variant="contained" size="large" onClick={() => { reset(); onDone(); }} sx={{ py: 1.5, bgcolor: C.aws, '&:hover': { bgcolor: '#c9541f' } }}>Siguiente pregunta</Button>
                        </Stack>
                    </>
                )}
            </Card>
        </Box>
    );
}

function Aws({ data, today, reload }) {
    const concepts = useMemo(() => (data.aws_concepts || []).map((c) => ({ ...c, next: parseDate(c.proximo) })), [data]);
    const log = useMemo(() => (data.aws_log || []).map((q) => ({ ...q, sent: parseDate(q.enviada), ans: parseDate(q.respondida) })).filter((q) => q.sent).sort((a, b) => b.sent - a.sent), [data]);
    const seen = concepts.filter((c) => c.estado !== 'Nuevo');
    const answered = log.filter((q) => q.resultado !== 'Pendiente');
    const ok = answered.filter((q) => q.resultado === 'Correcta').length;
    const pending = log.length - answered.length;
    const total = concepts.length || 63;
    const remaining = total - seen.length;

    const first = log.length ? log[log.length - 1].sent : null;
    const daysActive = first ? Math.max(1, daysBetween(first, today) + 1) : 0;
    const realPace = daysActive ? (seen.length / daysActive) * 7 : 0;

    const meta = data.meta;
    const target = NIVEL_PASO[meta?.nivel] || 3;
    const lead = daysToReach(0, target);
    const plan = useMemo(() => {
        if (!meta?.fecha) return null;
        const metaD = parseDate(meta.fecha);
        const ultimoNuevo = addDays(metaD, -lead);
        const disponibles = daysBetween(today, ultimoNuevo);
        const llegadaReal = addDays(today, (realPace > 0 ? Math.ceil((remaining / realPace) * 7) : 9999) + lead);
        if (disponibles <= 0 && remaining > 0) return { imposible: true, nivel: meta.nivel, meta: metaD, lead };
        const requerido = remaining > 0 ? (remaining / Math.max(1, disponibles)) * 7 : 0;
        const preguntasDia = requerido / 7 * (1 + target);
        // Misma fórmula que AWS Daily (n8n): correos al día = conceptos nuevos por día × aciertos para llegar al nivel, entre 1 y 8
        const correosDia = Math.min(8, Math.max(1, Math.ceil((requerido / 7) * target)));
        return { imposible: false, meta: metaD, ultimoNuevo, requerido, real: realPace, llegadaReal, preguntasDia, correosDia };
    }, [meta, lead, remaining, realPace, target, today]);

    const [rate, setRate] = useState(null);
    const [pickId, setPickId] = useState(null);
    const sliderRate = rate ?? Math.min(21, Math.max(1, Math.round(plan && !plan.imposible ? plan.requerido : realPace) || 1));
    const introEnd = addDays(today, Math.ceil((remaining / sliderRate) * 7));
    let ready = addDays(introEnd, lead);
    seen.forEach((c) => { if (c.paso >= target || c.estado === 'Dominada') return; const t0 = c.next && c.next > today ? c.next : today; const e = addDays(t0, daysToReach(c.paso, target)); if (e > ready) ready = e; });

    const byDom = {};
    answered.forEach((q) => { if (!EXAM_WEIGHTS[q.dominio]) return; byDom[q.dominio] = byDom[q.dominio] || { n: 0, ok: 0 }; byDom[q.dominio].n += 1; if (q.resultado === 'Correcta') byDom[q.dominio].ok += 1; });
    const canScore = answered.length >= 20 && DOMINIOS.every((d) => byDom[d] && byDom[d].n >= 3);
    const score = canScore ? Math.round(DOMINIOS.reduce((s, d) => s + (byDom[d].ok / byDom[d].n) * EXAM_WEIGHTS[d], 0) * 10) : null;

    let streak = 0; const firstPending = log.find((q) => q.resultado === 'Pendiente');
    if (firstPending) { for (const q of log) { if (q.resultado === 'Pendiente' && q.concepto === firstPending.concepto) streak += 1; else if (q.resultado !== 'Pendiente') break; } }
    const dueToday = seen.filter((c) => c.estado !== 'Dominada' && c.next && c.next <= today).length;
    const note = [
        pending ? `${pending} preguntas sin responder.` : null,
        streak >= 3 ? `“${firstPending.concepto}” se repitió ${streak} veces porque sigue sin respuesta.` : null,
        dueToday ? `${dueToday} conceptos tocan repaso hoy.` : null,
    ].filter(Boolean).join(' ');

    return (
        <Stack spacing={2}>
            <AnswerCard log={log} pickId={pickId} onPick={setPickId} onDone={reload} />

            <KpiRow note={note}>
                <Kpi value={seen.length} suffix={` / ${total}`} label="conceptos vistos" />
                <Kpi value={answered.length} suffix={` / ${log.length}`} label="preguntas respondidas" />
                <Kpi value={answered.length ? `${Math.round((ok / answered.length) * 100)}%` : '–'} label="aciertos" />
            </KpiRow>

            <GoalCard meta={meta} metaError={data.meta_error} plan={plan} onSaved={reload} />

            <Card title="¿Cuándo estarías listo?" desc={`Mueve el ritmo para ver cómo cambia la fecha. Supone que aciertas cada repaso hasta llegar a “${meta?.nivel || 'Paso 3'}”.`}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Conceptos nuevos por semana</Typography>
                    <Slider value={sliderRate} min={1} max={21} step={1} valueLabelDisplay="auto" onChange={(_, v) => setRate(v)}
                        sx={{ color: C.aws, flex: 1 }} aria-label="Conceptos nuevos por semana"
                        marks={plan && !plan.imposible && plan.requerido <= 21 ? [{ value: Math.round(plan.requerido), label: 'meta' }] : []} />
                    <Typography sx={{ fontWeight: 800, minWidth: 24 }}>{sliderRate}</Typography>
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                    <Big value={fmtDay.format(introEnd)} label={`terminas de ver los ${total} conceptos`} />
                    <Big value={fmtLong.format(ready)} label="listo para el examen" />
                    <Big value={meta?.fecha ? (ready <= parseDate(meta.fecha) ? '✓ Antes de tu meta' : `${daysBetween(parseDate(meta.fecha), ready)} días tarde`) : '—'} label="comparado con tu meta" />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {score != null
                        ? <>Con tus aciertos por dominio, hoy sacarías cerca de <b>{score} / 1000</b> (se aprueba con 720).</>
                        : `Para estimar tu puntaje del examen faltan respuestas: llevas ${answered.length} y se necesitan al menos 20, con 3 de cada dominio.`}
                </Typography>
            </Card>

            <Card title="Días que respondiste" desc="Últimos 21 días. Cada cuadro cuenta las preguntas de AWS Daily que respondiste.">
                <ActivityStrip dates={answered.map((q) => q.ans || q.sent)} label="preguntas respondidas" today={today} />
            </Card>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Card title="Avance por dominio del examen" desc="Oscuro: dominados. Claro: en aprendizaje. Gris: por ver.">
                    <Bars rows={DOMINIOS.map((dn) => {
                        const cs = concepts.filter((c) => c.dominio === dn);
                        const dom = cs.filter((c) => c.estado === 'Dominada').length;
                        const learn = cs.filter((c) => c.estado === 'Aprendiendo').length;
                        const b = byDom[dn];
                        return { name: dn, total: Math.max(1, cs.length), segs: [{ n: dom, color: C.seq[4] }, { n: learn, color: C.seq[2] }], num: `${dom + learn} / ${cs.length}${b ? ` · ${Math.round((b.ok / b.n) * 100)}% ok` : ''}` };
                    })} />
                </Card>
                <Card title="Conceptos en curso" desc="Los que ya empezaste, con su próximo repaso.">
                    <Table head={['Concepto', 'Aciertos', 'Fallos', 'Repaso']}
                        empty={!seen.length && <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>Todavía no empiezas ningún concepto.</Typography>}>
                        {[...seen].sort((a, b) => a.orden - b.orden).map((c) => (
                            <tr key={c.id}>
                                <td>{c.nombre} {c.foco && <Chip size="small" label="Foco" sx={{ ml: 0.5 }} />}</td>
                                <td className="n">{c.aciertos}</td>
                                <td className="n">{c.fallos}</td>
                                <td><NextChip next={c.next} done={c.estado === 'Dominada'} today={today} /></td>
                            </tr>
                        ))}
                    </Table>
                </Card>
            </Box>

            <Card title="Últimas preguntas" desc="Las 10 más recientes que te envió AWS Daily. Toca una sin responder para contestarla aquí.">
                <Table head={['Enviada', 'Concepto', 'Tipo', 'Resultado']}>
                    {log.slice(0, 10).map((q) => (
                        <tr key={q.id}>
                            <td className="n">{fmtDayTime.format(q.sent)}</td>
                            <td>{q.concepto}</td>
                            <td>{q.tipo}</td>
                            <td>{q.resultado === 'Correcta' ? <Chip size="small" color="success" variant="outlined" label="✓ Correcta" />
                                : q.resultado === 'Incorrecta' ? <Chip size="small" color="error" variant="outlined" label="✕ Incorrecta" />
                                    : <Chip size="small" color="warning" variant="outlined" label="Responder →" onClick={() => setPickId(q.id)} />}</td>
                        </tr>
                    ))}
                </Table>
            </Card>
        </Stack>
    );
}

/* ---------------------------------------------------------------- página */
export default function Estudio() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tab, setTab] = useState(() => { try { return localStorage.getItem('estudioTab') || 'aws'; } catch { return 'aws'; } });
    const today = startOfDay(new Date());

    const load = useCallback(async (fresh) => {
        setLoading(true); setError('');
        try {
            const { data: d } = await api.get('/estudio/', { params: fresh ? { fresh: 1 } : {} });
            setData(d);
        } catch (e) {
            setError(e?.response?.data?.message || 'No se pudo cargar el tablero.');
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(false); }, [load]);

    const changeTab = (_, v) => { setTab(v); try { localStorage.setItem('estudioTab', v); } catch { /* sin almacenamiento */ } };
    const logout = () => { localStorage.removeItem('accessToken'); localStorage.removeItem('refreshToken'); localStorage.removeItem('user'); navigate('/login'); };

    const errs = data?.errors || {};
    const fatal = Object.values(errs).find((e) => e.code === 'sin_token' || e.code === 'token_invalido');
    const secErr = (keys) => keys.map((k) => errs[k]).filter(Boolean);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#0b1220' }}>
                <Toolbar sx={{ gap: 1 }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>Panel privado</Typography>
                    <Button color="inherit" href="/panel">Solicitudes</Button>
                    <Button color="inherit" href="/panel/importar">Importar</Button>
                    <Button color="inherit" href="/panel/gratitud">Gratitud</Button>
                    <Button color="inherit" startIcon={<LogoutIcon />} onClick={logout}>Salir</Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ py: 5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} spacing={2} sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>Tablero de estudio</Typography>
                        <Typography color="text.secondary">Inglés y AWS SAA-C03, leído de tu Notion.</Typography>
                    </Box>
                    <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={() => load(true)} disabled={loading}>Actualizar</Button>
                </Stack>

                <Tabs value={tab} onChange={changeTab} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tab value="aws" label="AWS SAA-C03" />
                    <Tab value="en" label="Inglés" />
                </Tabs>

                {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => load(true)}>Reintentar</Button>}>{error}</Alert>}
                {fatal && <Alert severity="warning" sx={{ mb: 2 }}>{fatal.message}</Alert>}

                {!data && loading && <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>}

                {data && !fatal && (
                    <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity .2s' }}>
                        {tab === 'aws' && secErr(['aws_concepts', 'aws_log']).map((e) => <Alert key={e.message} severity="warning" sx={{ mb: 2 }}>{e.message}</Alert>)}
                        {tab === 'en' && secErr(['en_log', 'en_struct']).map((e) => <Alert key={e.message} severity="warning" sx={{ mb: 2 }}>{e.message}</Alert>)}
                        {tab === 'aws' ? <Aws data={data} today={today} reload={() => load(true)} /> : <English data={data} today={today} />}
                    </Box>
                )}
                {data?.generado && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>Datos de Notion leídos {fmtDayTime.format(new Date(data.generado.replace(/([+-]\d{2})(\d{2})$/, '$1:$2')))}.</Typography>}
            </Container>
        </Box>
    );
}
